import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Code2,
  Database,
  Globe,
  Server,
  Settings,
  Wrench
} from 'lucide-react'
import { skills, skillCategories } from '../../data/skills'

const categoryIcons: Record<string, typeof Code2> = {
  frontend: Globe,
  backend: Server,
  database: Database,
  devops: Settings,
  tools: Wrench
}

const categoryColors: Record<string, string> = {
  frontend: 'text-cyan-500',
  backend: 'text-violet-500',
  database: 'text-emerald-500',
  devops: 'text-amber-500',
  tools: 'text-rose-500'
}

const Skills = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  const filteredSkills = selectedCategory
    ? skills.filter(skill => skill.category === selectedCategory)
    : skills

  return (
    <section className="section section-border">
      <div className="container-wide">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0, 0, 0.2, 1] }}
          viewport={{ once: true }}
          className="mb-10 max-w-2xl"
        >
          <span className="section-label mb-4">Skills</span>
          <h2 className="text-balance mb-6">Tools I work with.</h2>
          <p className="text-lg text-zinc-500 dark:text-zinc-400">
            A practical toolkit built across frontend, backend, databases, DevOps, and security.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.05, ease: [0, 0, 0.2, 1] }}
          viewport={{ once: true }}
          className="mb-10 flex flex-wrap gap-2"
        >
          <button
            onClick={() => setSelectedCategory(null)}
            className={`rounded-lg px-3.5 py-1.5 text-sm font-medium transition-all duration-200 ${
              selectedCategory === null
                ? 'bg-zinc-950 text-white shadow-sm dark:bg-zinc-50 dark:text-zinc-950'
                : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-950 dark:text-zinc-400 dark:hover:bg-zinc-900 dark:hover:text-zinc-50'
            }`}
          >
            All
          </button>
          {skillCategories.map((category) => {
            const Icon = categoryIcons[category.id] || Code2
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-sm font-medium transition-all duration-200 ${
                  selectedCategory === category.id
                    ? 'bg-zinc-950 text-white shadow-sm dark:bg-zinc-50 dark:text-zinc-950'
                    : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-950 dark:text-zinc-400 dark:hover:bg-zinc-900 dark:hover:text-zinc-50'
                }`}
              >
                <Icon size={14} strokeWidth={1.5} />
                {category.name}
              </button>
            )
          })}
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={selectedCategory || 'all'}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2, ease: [0, 0, 0.2, 1] }}
            className="flex flex-wrap gap-3"
          >
            {filteredSkills.map((skill, index) => {
              const categoryName = skillCategories.find(cat => cat.id === skill.category)?.name

              return (
                <motion.div
                  key={skill.name}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.02, ease: [0, 0, 0.2, 1] }}
                  className="group flex items-center gap-2.5 rounded-lg border border-zinc-200 bg-white px-3 py-2 transition-all duration-200 hover:border-cyan-500/30 hover:shadow-md hover:shadow-cyan-500/5 dark:border-zinc-800 dark:bg-canvas-dark dark:hover:border-cyan-400/30"
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${categoryColors[skill.category]}`} />
                  <span className="text-sm font-medium text-zinc-950 dark:text-zinc-50">
                    {skill.name}
                  </span>
                  {selectedCategory === null && categoryName && (
                    <span className="hidden font-mono text-[9px] uppercase tracking-widest text-zinc-400 sm:inline">
                      {categoryName}
                    </span>
                  )}
                </motion.div>
              )
            })}
          </motion.div>
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0, 0, 0.2, 1] }}
          viewport={{ once: true }}
          className="mt-20 grid grid-cols-1 gap-5 md:grid-cols-3"
        >
          {[
            {
              title: 'Frontend',
              items: 'React, TypeScript, JavaScript, HTML5, CSS3, Tailwind CSS'
            },
            {
              title: 'Backend',
              items: 'Java, Spring Boot, Python, Node.js, Express, FastAPI'
            },
            {
              title: 'DevOps',
              items: 'Docker, Kubernetes, AWS, GitHub Actions, Jenkins'
            }
          ].map((group) => (
            <div
              key={group.title}
              className="card"
            >
              <h4 className="mb-2 text-sm font-semibold text-zinc-950 dark:text-zinc-50">
                {group.title}
              </h4>
              <p className="text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
                {group.items}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

export default Skills
